/**
 * ZoningCheck — OpenCounter-style address → zone → use clearance (live SF Planning GIS).
 * Shares citizen chrome / portal nav with the permit advisor so it feels like one product.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ZoningMap from '../components/zoning/ZoningMap';
import {
  checkSfLandUseApi,
  lookupSfParcelApi,
} from '../api/applicationType.api';
import axiosClient from '../api/axios.client';
import CitizenProductChrome, {
  buildSfCitizenPortalLinks,
  pickAdvisorPortalForUse,
} from '../components/citizen/CitizenProductChrome';

const TEAL = '#1976d2'; // InterviewFlow accent (landing / demos / publicSector)

const STATUS_CHIP = {
  permitted: { bgcolor: '#e8f5e9', color: '#1b5e20', border: '#66bb6a' },
  conditional: { bgcolor: '#fff8e1', color: '#e65100', border: '#ffb74d' },
  not_permitted: { bgcolor: '#ffebee', color: '#b71c1c', border: '#ef5350' },
  unknown: { bgcolor: '#f5f5f5', color: '#616161', border: '#bdbdbd' },
};

function statusStyle(status) {
  const key = String(status || 'unknown').toLowerCase();
  if (key.includes('not')) return STATUS_CHIP.not_permitted;
  if (key.includes('condit')) return STATUS_CHIP.conditional;
  if (key.includes('permit')) return STATUS_CHIP.permitted;
  return STATUS_CHIP.unknown;
}

async function fetchLandUseSuggestions(q) {
  const data = await axiosClient.get('/application-type/land-uses', {
    params: { q: q || '', jurisdictionId: 'san-francisco' },
  });
  return data?.landUses || [];
}

export default function ZoningCheckPage() {
  const theme = useTheme();
  const isSff = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const cityParam = String(searchParams.get('city') || searchParams.get('jurisdiction') || '')
    .trim()
    .toLowerCase();
  const isSf = !cityParam || cityParam === 'san-francisco';
  const [address, setAddress] = useState(() => String(searchParams.get('address') || '').trim());
  const [proposedUse, setProposedUse] = useState(null);
  const [useInput, setUseInput] = useState(() => String(
    searchParams.get('use') || searchParams.get('description') || '',
  ).trim());
  const [useOptions, setUseOptions] = useState([]);
  const [loadingUses, setLoadingUses] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [parcel, setParcel] = useState(null);
  const [clearance, setClearance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const q = useInput.trim();
    const t = setTimeout(async () => {
      setLoadingUses(true);
      try {
        const list = await fetchLandUseSuggestions(q);
        if (!cancelled) setUseOptions(list);
      } catch (_) {
        if (!cancelled) setUseOptions([]);
      } finally {
        if (!cancelled) setLoadingUses(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [useInput]);

  const handleCheck = useCallback(async () => {
    const addr = address.trim();
    if (addr.length < 5) {
      setError('Enter a San Francisco street address (at least 5 characters).');
      return;
    }
    setBusy(true);
    setError(null);
    setClearance(null);
    try {
      const useQuery = proposedUse?.label || useInput.trim() || undefined;
      if (useQuery) {
        const result = await checkSfLandUseApi({
          address: addr,
          useQuery,
          jurisdictionId: 'san-francisco',
        });
        setParcel(result?.parcel || null);
        setClearance(result);
        if (!result?.parcel?.success && result?.parcel?.error) {
          setError(result.parcel.error);
        } else if (!result?.parcel?.success && !result?.zoningCode) {
          setError(result?.parcel?.message || 'Could not resolve parcel zoning for that address.');
        } else {
          import('../utils/discoverySession').then(({ trackDiscovery }) => {
            trackDiscovery('zoning_check', {
              packageId: 'san-francisco',
              meta: { status: result?.status || null },
            });
          }).catch(() => {});
        }
      } else {
        const result = await lookupSfParcelApi({ address: addr });
        setParcel(result);
        if (!result?.success) {
          setError(result?.message || result?.error || 'Parcel lookup failed.');
        } else {
          import('../utils/discoverySession').then(({ trackDiscovery }) => {
            trackDiscovery('zoning_check', { packageId: 'san-francisco' });
          }).catch(() => {});
        }
      }
    } catch (e) {
      setParcel(null);
      setClearance(null);
      setError(e?.response?.data?.message || e?.message || 'Lookup failed');
    } finally {
      setBusy(false);
    }
  }, [address, proposedUse, useInput]);

  const zoning = parcel?.zoning || (clearance ? {
    code: clearance.zoningCode,
    name: clearance.zoningName,
  } : null);

  const marker = useMemo(() => {
    const lat = parcel?.geocode?.lat ?? parcel?.lat ?? parcel?.location?.lat;
    const lng = parcel?.geocode?.lon ?? parcel?.lon ?? parcel?.lng
      ?? parcel?.location?.lng ?? parcel?.location?.lon;
    if (lat == null || lng == null) return null;
    return {
      lat: Number(lat),
      lng: Number(lng),
      label: parcel?.geocode?.matchedAddress
        || parcel?.location
        || parcel?.parcel?.streetAddress
        || address,
    };
  }, [parcel, address]);

  const mapCenter = marker ? [marker.lat, marker.lng] : [37.7749, -122.4194];
  const constraints = Array.isArray(parcel?.constraints) ? parcel.constraints.slice(0, 6) : [];
  const chip = statusStyle(clearance?.status);
  const useLabel = (proposedUse?.label || useInput).trim();
  const advisorPortal = pickAdvisorPortalForUse(useLabel);
  const advisorQuery = new URLSearchParams();
  if (address.trim()) advisorQuery.set('address', address.trim());
  if (useLabel) {
    advisorQuery.set('description', useLabel);
    advisorQuery.set('proposedUse', useLabel);
  }
  const advisorHref = `/v2/demo/permit/${advisorPortal}${advisorQuery.toString() ? `?${advisorQuery}` : ''}`;
  const portalLinks = buildSfCitizenPortalLinks({ active: 'zoning' });

  if (!isSf) {
    const advisorTo = `/v2/demo/permit?city=${encodeURIComponent(cityParam)}`;
    return (
      <CitizenProductChrome
        cityName="InterviewFlow"
        title="Zoning check"
        subtitle="Live parcel GIS is San Francisco only."
        portalLinks={buildSfCitizenPortalLinks({
          active: 'zoning',
          cityQs: `?city=${encodeURIComponent(cityParam)}`,
          includeZoning: true,
        })}
        primaryColor={TEAL}
        testId="zoning-check-page"
        banner={(
          <>
            Live zoning / parcel GIS is available for
            {' '}
            <strong>San Francisco</strong>
            {' '}
            only.
            {' '}
            <strong>{cityParam}</strong>
            {' '}
            uses catalog-based application guidance.
          </>
        )}
      >
        <Box sx={{ p: 3, maxWidth: 520 }}>
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to={advisorTo} variant="contained" sx={{ textTransform: 'none' }}>
              Open
              {' '}
              {cityParam}
              {' '}
              advisor
            </Button>
            <Button component={RouterLink} to="/zoning-check" variant="outlined" sx={{ textTransform: 'none' }}>
              SF zoning check
            </Button>
          </Stack>
        </Box>
      </CitizenProductChrome>
    );
  }

  const resultsCard = (parcel?.success || zoning?.code) ? (
    <Box
      sx={{
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
      data-testid="zoning-check-results"
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <MapIcon sx={{ color: 'var(--citizen-primary, #1565c0)', fontSize: 20 }} />
        <Typography
          variant="subtitle1"
          fontWeight={650}
          sx={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Parcel zoning
        </Typography>
      </Stack>
      {(parcel?.geocode?.matchedAddress || parcel?.location || parcel?.parcel?.streetAddress) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {parcel.geocode?.matchedAddress || parcel.location || parcel.parcel?.streetAddress}
          {parcel.parcel?.mapblklot ? ` · Block/lot ${parcel.parcel.mapblklot}` : ''}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Chip
          label={zoning?.code || 'Unknown zone'}
          sx={{
            fontWeight: 700,
            bgcolor: 'color-mix(in srgb, var(--citizen-primary, #1565c0) 12%, #fff)',
            color: 'var(--citizen-primary, #1565c0)',
            border: '1px solid',
            borderColor: 'var(--citizen-primary, #1565c0)',
          }}
        />
        {zoning?.name && <Typography variant="body2">{zoning.name}</Typography>}
        {zoning?.general && (
          <Chip size="small" variant="outlined" label={zoning.general} />
        )}
      </Stack>

      {clearance && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Use clearance
          </Typography>
          <Chip
            label={clearance.statusLabel || clearance.status || 'Unknown'}
            sx={{
              fontWeight: 650,
              bgcolor: chip.bgcolor,
              color: chip.color,
              border: `1px solid ${chip.border}`,
              mb: 1,
            }}
          />
          {clearance.use?.label && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Catalog match:
              {' '}
              <strong>{clearance.use.label}</strong>
            </Typography>
          )}
          {clearance.rationale && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45 }}
              dangerouslySetInnerHTML={{
                __html: String(clearance.rationale)
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
              }}
            />
          )}
        </Box>
      )}

      {constraints.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
            Site constraints
          </Typography>
          <Stack spacing={0.5}>
            {constraints.map((c) => (
              <Typography key={c.id || c.label} variant="body2">
                <Chip
                  size="small"
                  label={c.severity || 'info'}
                  sx={{ mr: 0.75, height: 20, fontSize: '0.65rem' }}
                />
                {c.label}
                {c.detail ? ` — ${c.detail}` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      <Alert severity="info" sx={{ mb: 1.25, py: 0.5 }}>
        Live SF Planning GIS — guidance only.
        {' '}
        <Link href="https://sfplanninggis.org/pim/" target="_blank" rel="noopener noreferrer">
          Property Information Map
        </Link>
      </Alert>

      <Button
        component={RouterLink}
        to={advisorHref}
        variant="contained"
        fullWidth
        endIcon={<ArrowForwardIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 650,
          bgcolor: 'var(--citizen-primary, #1565c0)',
          '&:hover': { bgcolor: 'var(--citizen-primary, #1565c0)', filter: 'brightness(0.92)' },
        }}
        data-testid="zoning-continue-advisor"
      >
        Continue in
        {' '}
        {advisorPortal}
        {' '}
        advisor
      </Button>
    </Box>
  ) : null;

  return (
    <CitizenProductChrome
      cityName="City and County of San Francisco"
      title="Zoning check"
      subtitle="Look up parcel zoning and whether a proposed use looks permitted, conditional, or not — then continue into the application advisor."
      portalLinks={portalLinks}
      primaryColor={TEAL}
      testId="zoning-check-page"
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: isSff ? '1fr' : 'minmax(320px, 0.95fr) minmax(360px, 1.15fr)',
          gridTemplateRows: isSff ? 'auto' : '1fr',
          overflow: isSff ? 'auto' : 'hidden',
        }}
      >
        {/* Left: intake + results (same column role as advisor form) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: isSff ? 'visible' : 'auto',
            borderRight: isSff ? 0 : 1,
            borderBottom: isSff ? 1 : 0,
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 0.35 }}
            >
              Site & proposed use
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Same SF Planning GIS the advisor uses for pin clearance — focused on zoning first.
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Street address"
                placeholder="e.g. 1650 Mission St, San Francisco, CA"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !busy && handleCheck()}
                disabled={busy}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={useOptions}
                loading={loadingUses}
                value={proposedUse}
                inputValue={useInput}
                onChange={(_, v) => {
                  setProposedUse(typeof v === 'string' ? { label: v } : v);
                  if (v && typeof v === 'object') setUseInput(v.label || '');
                }}
                onInputChange={(_, v) => {
                  setUseInput(v);
                  if (!v) setProposedUse(null);
                }}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o?.label || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Proposed use (optional)"
                    placeholder="restaurant, ADU, yoga studio…"
                    helperText="Blank = zoning only; add a use for permitted / conditional / not."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUses ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={handleCheck}
                disabled={busy}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 650,
                  bgcolor: 'var(--citizen-primary, #1565c0)',
                  '&:hover': { bgcolor: 'var(--citizen-primary, #1565c0)', filter: 'brightness(0.92)' },
                  minWidth: 180,
                }}
              >
                {busy ? 'Checking…' : 'Check zoning'}
              </Button>
            </Stack>
            {error && (
              <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
          {resultsCard}
        </Box>

        {/* Right: map (same column role as advisor parcel map) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: isSff ? 280 : 0,
            bgcolor: '#fafbfc',
          }}
        >
          <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Parcel & clearance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {marker
                ? 'Parcel pin from geocode + Planning GIS.'
                : 'Enter an SF address to pin the parcel.'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: isSff ? 260 : 0, p: 1 }}>
            <ZoningMap
              center={mapCenter}
              zoom={marker ? 17 : 13}
              height={isSff ? 280 : 520}
              marker={marker}
              showPlaceholderOverlay={false}
            />
          </Box>
        </Box>
      </Box>
    </CitizenProductChrome>
  );
}
